import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

interface AdminUser {
    id: number
    username: string
    email?: string
}

interface AdminAuthContextType {
    user: AdminUser | null
    isLoading: boolean
    isAuthenticated: boolean
    login: (username: string, password: string) => Promise<boolean>
    logout: () => void
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api"

export function AdminAuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AdminUser | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        // Check for existing auth token
        const token = localStorage.getItem("admin_token")
        if (token) {
            // Verify token and get user info
            fetchUser(token)
        } else {
            setIsLoading(false)
        }
    }, [])

    const fetchUser = async (token: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/me/`, {
                headers: {
                    Authorization: `Token ${token}`,
                },
            })
            if (response.ok) {
                const userData = await response.json()
                setUser(userData)
            } else {
                localStorage.removeItem("admin_token")
            }
        } catch (error) {
            console.error("Failed to fetch user:", error)
            localStorage.removeItem("admin_token")
        } finally {
            setIsLoading(false)
        }
    }

    const login = async (username: string, password: string): Promise<boolean> => {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/login/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ username, password }),
            })

            if (response.ok) {
                const data = await response.json()
                localStorage.setItem("admin_token", data.token)
                setUser(data.user)
                return true
            }
            return false
        } catch (error) {
            console.error("Login failed:", error)
            return false
        }
    }

    const logout = () => {
        localStorage.removeItem("admin_token")
        setUser(null)
        window.location.href = "/admin/login"
    }

    return (
        <AdminAuthContext.Provider
            value={{
                user,
                isLoading,
                isAuthenticated: !!user,
                login,
                logout,
            }}
        >
            {children}
        </AdminAuthContext.Provider>
    )
}

export function useAdminAuth() {
    const context = useContext(AdminAuthContext)
    if (context === undefined) {
        throw new Error("useAdminAuth must be used within an AdminAuthProvider")
    }
    return context
}
